import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-slate-300">Last updated: May 21, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-slate max-w-none">
          <p className="text-slate-700">
            Progress Insurance Solutions LLC ("Progress Insurance Solutions," "we," "us," or "our") values your privacy. This Privacy Policy describes how we collect, use, and share personal information when you visit{' '}
            <a href="https://progressinsure.com" className="text-[#C9A361] hover:underline">https://progressinsure.com</a> (the "Site"), contact us, or use our services.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">1. Information We Collect</h2>
          <p className="text-slate-700">We may collect the following categories of personal information:</p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2">
            <li><strong>Contact details</strong> you provide: first name, last name, email address, phone number, mailing address.</li>
            <li><strong>Insurance-related information</strong> you submit through our quote form, such as the type of coverage you are requesting and any details you include in the message field.</li>
            <li><strong>Communications</strong>: records of phone calls, emails, SMS messages, and other correspondence you exchange with us.</li>
            <li><strong>Technical data</strong> automatically collected when you visit the Site, such as IP address, browser type, device information, pages viewed, and approximate location.</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">2. How We Use Your Information</h2>
          <p className="text-slate-700">We use your personal information to:</p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2">
            <li>Respond to quote requests and customer service inquiries;</li>
            <li>Provide insurance products and services and communicate about your policies;</li>
            <li>Send appointment reminders, policy updates, account notifications, and other transactional messages by phone, email, or SMS;</li>
            <li>Improve our website, services, and customer experience;</li>
            <li>Comply with legal, regulatory, and insurance-licensing obligations;</li>
            <li>Detect and prevent fraud or unauthorized activity.</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">3. How We Share Your Information</h2>
          <p className="text-slate-700">We share personal information only as needed to provide our services and as described below:</p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2">
            <li><strong>Insurance carriers and underwriters</strong> we work with, in order to obtain quotes and bind coverage on your behalf;</li>
            <li><strong>Service providers</strong> who help us operate our business (for example, email, SMS, hosting, and CRM providers), under written agreements requiring confidentiality;</li>
            <li><strong>Legal and regulatory authorities</strong> when required by law, subpoena, or to protect our rights and the safety of others.</li>
          </ul>
          <p className="text-slate-700 mt-4 font-semibold bg-[#F5F1E8] border-l-4 border-[#C9A361] p-4">
            SMS consent is not shared with third parties. We do not sell, rent, or share your mobile phone number or SMS opt-in information with third parties or affiliates for their marketing purposes.
          </p>
          <p className="text-slate-700 mt-4">
            We do not sell your personal information.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">4. SMS / Text Messaging</h2>
          <p className="text-slate-700">
            If you opt in to receive SMS messages from Progress Insurance Solutions LLC, you are agreeing to receive messages that may include quote follow-ups, appointment reminders, policy notifications, and customer service updates.
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2">
            <li>Messaging frequency may vary.</li>
            <li>Message and data rates may apply.</li>
            <li>To opt out at any time, reply <strong>STOP</strong> to any message.</li>
            <li>For help, reply <strong>HELP</strong> or contact us at <a href="mailto:contact@progressinsure.com" className="text-[#C9A361] hover:underline">contact@progressinsure.com</a>.</li>
            <li>Full SMS terms are available on our <a href="/terms-of-service" className="text-[#C9A361] hover:underline">Terms of Service</a> page.</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">5. Cookies and Tracking</h2>
          <p className="text-slate-700">
            We use basic cookies and similar technologies to operate the Site, remember your preferences, and analyze how visitors use our pages. You can control cookies through your browser settings.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">6. Data Retention</h2>
          <p className="text-slate-700">
            We retain personal information for as long as necessary to provide our services, comply with legal and recordkeeping obligations, resolve disputes, and enforce our agreements.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">7. Your Choices and Rights</h2>
          <ul className="list-disc pl-6 text-slate-700 space-y-2">
            <li>You may unsubscribe from marketing emails at any time using the link in the email.</li>
            <li>You may opt out of SMS messages at any time by replying STOP.</li>
            <li>Depending on where you live, you may have rights to access, correct, or request deletion of your personal information. Contact us using the details below to exercise these rights.</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">8. Security</h2>
          <p className="text-slate-700">
            We use reasonable administrative, technical, and physical safeguards to protect your information. No method of transmission or storage is 100% secure, but we work to protect your data and limit access to authorized personnel.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">9. Children's Privacy</h2>
          <p className="text-slate-700">
            Our services are intended for adults. We do not knowingly collect personal information from children under 13.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">10. Changes to This Policy</h2>
          <p className="text-slate-700">
            We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page indicates when it was last revised. Continued use of our Site or services after changes are posted constitutes acceptance of the updated policy.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">11. Contact Us</h2>
          <p className="text-slate-700">
            If you have any questions about this Privacy Policy or how your information is handled, please contact us:
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mt-4">
            <p className="font-semibold text-slate-900">Progress Insurance Solutions LLC</p>
            <p className="text-slate-700">Coral Springs: 3111 N University Dr Ste 404, Coral Springs, FL 33065</p>
            <p className="text-slate-700">Maitland: 500 N Maitland Ave Ste 304, Maitland, FL 32751</p>
            <p className="text-slate-700">Phone: <a href="tel:954-495-2929" className="text-[#C9A361] hover:underline">954-495-2929</a></p>
            <p className="text-slate-700">Email: <a href="mailto:contact@progressinsure.com" className="text-[#C9A361] hover:underline">contact@progressinsure.com</a></p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
